import type {ReactElement, ReactNode} from 'react';
import React from 'react';
import type {GeneratedImageLightboxHeaderContentProps} from './generated-image-lightbox-header-content.types';
import styles from './generated-image-lightbox-header-content.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@/components/dropdown-v2/dropdown.types';
import {openAiChatImageDownloadModal} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';
import {LoadingStates} from '@Utils/loading.util';
import {useOpenChatImageInEditor} from '@Components/ai-chat-window/hooks/useOpenChatImageInEditor';
import {usePublishChatModelGeneratedImage} from '@Components/ai-chat-window/hooks/usePublishChatModelGeneratedImage';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7/controlled-list-item-type-7';
import {useResetAiActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAiActionLoadingStatesOnModalClose';

export function GeneratedImageLightboxHeaderContent({
  onClose,
  hashedId,
  chatId,
  canWrite,
  onReattach,
  currentImage,
  currentImageSrc,
}: GeneratedImageLightboxHeaderContentProps): ReactElement | null {
  const {state, actions} = useAiChat();
  const {openPosterInEditor} = useOpenChatImageInEditor({chatId});
  const {publishToSocialMedia, publishAsEmailCampaign, promoteAsEvent} = usePublishChatModelGeneratedImage(chatId, hashedId);

  useResetAiActionLoadingStatesOnModalClose(chatId, hashedId);

  const getLoadingStatesForImageActions = (): GeneratedImagePartActionsLoading => {
    return state.getLoadingStatesForImageActions(chatId, hashedId);
  };

  const handleCloseLightbox = (): void => {
    onClose();
  };

  const handleDownload = (): void => {
    const hashedIdValue: string | undefined = hashedId;
    if (!hashedIdValue) {
      return;
    }

    openAiChatImageDownloadModal(hashedIdValue, chatId);
  };

  const isDisabled =
    getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().openEditorLoadingState === LoadingStates.LOADING;

  const getMoreOptionsDropdown = (): ReactNode => {
    return (
      <Dropdown
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_options')}
        popupClassName={styles.actionPopup}
        className={styles.positionUnset}
        doCloseOnInnerClick
        selector={<Icon icon="icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={isDisabled} />}
        popup={
          <>
            <ControlledListItemType7
              id="edit-in-editor"
              text={window.i18next.t('pmwjs_edit_image_in_editor')}
              icon="icon-pencil"
              onClick={(): void => {
                void openPosterInEditor(hashedId);
              }}
              isLoading={getLoadingStatesForImageActions().openEditorLoadingState === LoadingStates.LOADING}
              isDisabled={isDisabled || !canWrite}
              className="radius-8"
            />
            <ControlledListItemType7
              id="reattach"
              text={window.i18next.t('pmwjs_reattach')}
              icon="icon-paperclip"
              onClick={(): void => {
                onReattach(currentImage, currentImageSrc);
                handleCloseLightbox();
              }}
              isDisabled={isDisabled}
              className="radius-8"
            />
          </>
        }
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
      />
    );
  };

  const getDownloadAction = (): ReactNode => {
    if (state.isSmall) {
      return (
        <Icon
          icon="icon-download"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          onClick={handleDownload}
          isDisabled={isDisabled}
          isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
        />
      );
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_download')}
        size={Size.XSMALL}
        type={Type.DARK_SECONDARY}
        icon="icon-download"
        onClick={handleDownload}
        isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
        disabled={isDisabled}
      />
    );
  };

  const getEditAndReattachActions = (): ReactNode => {
    if (state.isSmall) {
      return getMoreOptionsDropdown();
    }

    return (
      <>
        <Button
          text={window.i18next.t('pmwjs_edit_image_in_editor')}
          size={Size.XSMALL}
          type={Type.DARK_GHOST}
          icon="icon-pencil"
          onClick={(): void => {
            void openPosterInEditor(hashedId);
          }}
          isLoading={getLoadingStatesForImageActions().openEditorLoadingState === LoadingStates.LOADING}
          disabled={isDisabled || !canWrite}
        />
        <Button
          text={window.i18next.t('pmwjs_reattach')}
          size={Size.XSMALL}
          type={Type.DARK_GHOST}
          icon="icon-paperclip"
          onClick={(): void => {
            onReattach(currentImage, currentImageSrc);
            handleCloseLightbox();
          }}
          disabled={isDisabled}
        />
      </>
    );
  };

  const getLightboxHeader = (): ReactElement => {
    return (
      <div className={styles.lightboxHeader}>
        <Icon
          icon="icon-arrow-left"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_GHOST}
          ariaLabel={window.i18next.t('pmwjs_back')}
          shape={IconShape.SQUARE}
          onClick={handleCloseLightbox}
          className={styles.backButton}
        />
        <div className={`flex-h-row gap-2 ${styles.headerActions}`}>
          {getEditAndReattachActions()}
          {getDownloadAction()}
          <Dropdown
            showModalForSmallScreen
            modalTitle={window.i18next.t('pmwjs_publish')}
            popupClassName={styles.actionPopup}
            className={styles.positionUnset}
            selector={
              <Button
                text={window.i18next.t('pmwjs_publish')}
                size={Size.XSMALL}
                type={Type.SECONDARY}
                icon="icon-publish-arrow"
                isLoading={getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING}
                disabled={isDisabled || !canWrite}
              />
            }
            popup={
              <PublishingOptions
                chatId={chatId}
                hashedId={hashedId}
                loadingStates={getLoadingStatesForImageActions()}
                publishToSocialMedia={publishToSocialMedia}
                publishAsEmailCampaign={publishAsEmailCampaign}
                promoteAsEvent={promoteAsEvent}
              />
            }
            alignment={ALIGNMENT_TYPE.BOTTOM_END}
          />
        </div>
      </div>
    );
  };

  return getLightboxHeader();
}
